import * as React from 'react';
import { useAuth } from '../contexts/AuthProvider';
import { useNavigation } from '../contexts/NavigationProvider';
import { useOpportunities } from '../contexts/OpportunitiesProvider';
import type { Opportunity, Application, ApplicationStatus } from '../types';
import BriefcaseIcon from './icons/BriefcaseIcon';
import TrashIcon from './icons/TrashIcon';
import PlusCircleIcon from './icons/PlusCircleIcon';
import MapPinIcon from './icons/MapPinIcon';

const statusColors: Record<ApplicationStatus, string> = {
  draft: 'bg-gray-500/20 text-gray-300',
  submitted: 'bg-yellow-500/20 text-yellow-300',
  reviewed: 'bg-blue-500/20 text-blue-300',
  shortlisted: 'bg-indigo-500/20 text-indigo-300',
  accepted: 'bg-green-500/20 text-green-300',
  rejected: 'bg-red-500/20 text-red-300',
};

const statusText: Record<ApplicationStatus, string> = {
    draft: 'Draft',
    submitted: 'Submitted',
    reviewed: 'Reviewed',
    shortlisted: 'Shortlisted',
    accepted: 'Accepted',
    rejected: 'Rejected',
};

const MyActivityPage: React.FC = () => {
    const [activeTab, setActiveTab] = React.useState<'applied' | 'posted' | 'attending'>('applied');
    const { currentUser, managedProfile } = useAuth();
    const { opportunities, deleteOpportunity, myApplications, isSyncingApps } = useOpportunities();
    const { handleViewApplication, handleViewApplicantsForOpportunity, handleViewOpportunity } = useNavigation();

    const effectiveHandle = managedProfile?.handle ?? currentUser?.handle;

    const myPostedOpportunities = React.useMemo(() => {
        if (!effectiveHandle) return [];
        return opportunities
            .filter(opp => opp.author.handle === effectiveHandle)
            .sort((a,b) => b.timestamp.localeCompare(a.timestamp));
    }, [opportunities, effectiveHandle]);

    const myAttendingOpportunities = React.useMemo(() => {
        if (!effectiveHandle) return [];
        return opportunities
            .filter(opp => (opp.attendees || []).some(a => a.handle === effectiveHandle))
            .sort((a,b) => (a.startDate || a.timestamp).localeCompare(b.startDate || b.timestamp));
    }, [opportunities, effectiveHandle]);

    if (!currentUser) return null;

    return (
        <div className="space-y-6 font-urbanist">
            <div className="bg-brand-surface rounded-lg p-6 border border-gray-800 shadow-sm">
                <div className="flex items-center space-x-3">
                    <BriefcaseIcon className="h-7 w-7 text-brand-orange" />
                    <h2 className="text-3xl font-black text-white tracking-tighter uppercase">My Activity</h2>
                </div>
                 <div className="mt-4 border-b border-gray-700">
                    <nav className="-mb-px flex space-x-6" aria-label="Tabs">
                        <button
                            onClick={() => setActiveTab('applied')}
                            className={`whitespace-nowrap py-3 px-1 border-b-2 font-black text-sm transition-colors uppercase tracking-widest ${
                                activeTab === 'applied'
                                ? 'border-brand-orange text-brand-orange'
                                : 'border-transparent text-brand-muted hover:text-white hover:border-gray-500'
                            }`}
                        >
                            Applied ({myApplications.length})
                        </button>
                        <button
                             onClick={() => setActiveTab('posted')}
                             className={`whitespace-nowrap py-3 px-1 border-b-2 font-black text-sm transition-colors uppercase tracking-widest ${
                                activeTab === 'posted'
                                ? 'border-brand-orange text-brand-orange'
                                : 'border-transparent text-brand-muted hover:text-white hover:border-gray-500'
                            }`}
                        >
                            Posted ({myPostedOpportunities.length})
                        </button>
                        <button
                             onClick={() => setActiveTab('attending')}
                             className={`whitespace-nowrap py-3 px-1 border-b-2 font-black text-sm transition-colors uppercase tracking-widest ${
                                activeTab === 'attending'
                                ? 'border-brand-orange text-brand-orange'
                                : 'border-transparent text-brand-muted hover:text-white hover:border-gray-500'
                            }`}
                        >
                            Attending ({myAttendingOpportunities.length})
                        </button>
                    </nav>
                </div>
            </div>

            {activeTab === 'applied' && (
                 <AppliedTabContent 
                    isLoading={isSyncingApps}
                    myApplications={myApplications} 
                    handleViewApplication={handleViewApplication} 
                />
            )}

            {activeTab === 'posted' && (
                <PostedTabContent
                    myPostedOpportunities={myPostedOpportunities}
                    handleViewApplicants={handleViewApplicantsForOpportunity}
                    deleteOpportunity={deleteOpportunity}
                />
            )}

            {activeTab === 'attending' && (
                <AttendingTabContent
                    myAttendingOpportunities={myAttendingOpportunities}
                    handleViewOpportunity={handleViewOpportunity}
                />
            )}
        </div>
    );
};

// --- Applied Tab Content ---
interface AppliedTabProps {
    isLoading: boolean;
    myApplications: { opportunity: Opportunity, application: Application }[];
    handleViewApplication: (app: Application, opp: Opportunity) => void;
}
const AppliedTabContent: React.FC<AppliedTabProps> = ({ isLoading, myApplications, handleViewApplication }) => (
    <>
        {isLoading && myApplications.length === 0 ? (
            <div className="bg-brand-surface rounded-lg p-12 text-center animate-pulse border border-gray-800 shadow-sm">
                <p className="text-brand-muted font-bold uppercase tracking-widest">Syncing your stage activity...</p>
            </div>
        ) : myApplications.length === 0 ? (
            <div className="bg-brand-surface rounded-lg p-12 text-center border border-dashed border-gray-800 shadow-sm">
                <BriefcaseIcon className="h-16 w-16 text-gray-700 mx-auto mb-4 opacity-30" />
                <h3 className="text-xl font-black text-white mb-2 tracking-tight uppercase">No applications yet</h3>
                <p className="text-brand-muted max-w-sm mx-auto font-medium">Explore the feed to find your next gig and put your talent on display.</p>
            </div>
        ) : (
            <div className="bg-brand-surface rounded-lg p-4 border border-gray-800 shadow-sm">
                <ul className="space-y-3">
                    {myApplications.map(({ opportunity, application }) => {
                        // Fallback logic for displaying "Applied As"
                        const applyingAsBands = application.applyingAs?.bands || [];
                        const bandNames = applyingAsBands.map(b => b.name).join(', ');
                        const entityDisplay = bandNames 
                            ? `${bandNames}${application.applyingAs?.individual ? ' & Me' : ''}`
                            : 'Individual';

                        return (
                            <li key={application.id}>
                                <div 
                                    className="block p-5 bg-gray-800/30 hover:bg-gray-800/60 border border-gray-700 hover:border-brand-orange/30 rounded-2xl cursor-pointer transition-all"
                                    onClick={() => handleViewApplication(application, opportunity)}
                                >
                                    <div className="flex justify-between items-start flex-wrap gap-2">
                                        <div>
                                            <h3 className="font-black text-lg text-white tracking-tight uppercase">{opportunity.title}</h3>
                                            <p className="text-xs text-brand-orange font-bold uppercase tracking-widest mt-1">by {opportunity.author.name}</p>
                                            <p className="text-[10px] text-brand-muted font-black uppercase tracking-widest mt-3">Applied As: <span className="text-gray-300">{entityDisplay}</span></p>
                                        </div>
                                        <div className={`px-3 py-1 text-[10px] font-black uppercase tracking-widest rounded-full border ${statusColors[application.status]} border-current shadow-sm`}>
                                            {statusText[application.status]}
                                        </div>
                                    </div>
                                    <div className="mt-6 pt-4 border-t border-gray-800/50 flex justify-between items-center">
                                        <p className="text-[10px] text-brand-muted font-black uppercase tracking-widest">
                                            {new Date(application.timestamp).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                                        </p>
                                        <span className="text-[10px] text-brand-orange font-black uppercase tracking-widest group-hover:underline">View Status &rarr;</span>
                                    </div>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            </div>
        )}
    </>
);


// --- Posted Tab Content ---
interface PostedTabProps {
    myPostedOpportunities: Opportunity[];
    handleViewApplicants: (opp: Opportunity) => void;
    deleteOpportunity: (id: string) => Promise<void>;
}
const PostedTabContent: React.FC<PostedTabProps> = ({ myPostedOpportunities, handleViewApplicants, deleteOpportunity }) => (
     <>
        {myPostedOpportunities.length === 0 ? (
            <div className="bg-brand-surface rounded-lg p-12 text-center border border-dashed border-gray-800 shadow-sm">
                <PlusCircleIcon className="h-16 w-16 text-gray-700 mx-auto mb-4 opacity-30" />
                <h3 className="text-xl font-black text-white mb-2 tracking-tight uppercase">No posts yet</h3>
                <p className="text-brand-muted max-w-sm mx-auto font-medium">Found a role that needs filling? Post an opportunity to scout top talent in the network.</p>
            </div>
        ) : (
            <div className="bg-brand-surface rounded-lg p-4 border border-gray-800 shadow-sm">
                <ul className="space-y-3">
                    {myPostedOpportunities.map(opp => (
                        <li key={opp.id}>
                            <div className="block p-5 bg-gray-800/30 border border-gray-700 rounded-2xl">
                                <div className="flex justify-between items-start flex-wrap gap-2">
                                    <div className="flex-1 cursor-pointer" onClick={() => handleViewApplicants(opp)}>
                                        <h3 className="font-black text-lg text-white tracking-tight hover:text-brand-orange transition-colors uppercase">{opp.title}</h3>
                                        <p className="text-xs text-brand-muted font-bold uppercase tracking-widest mt-1">
                                            {opp.applicationCount ?? 0} active applicant(s)
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className={`px-3 py-1 text-[10px] font-black uppercase tracking-widest rounded-full border ${opp.status === 'open' ? 'bg-green-500/10 text-green-400 border-green-500/30' : 'bg-red-500/10 text-red-400 border-red-500/30'}`}>
                                            {opp.status}
                                        </div>
                                         <button onClick={() => deleteOpportunity(opp.id)} className="p-2 text-red-400 hover:bg-red-500/10 rounded-full transition-colors" aria-label="Delete opportunity">
                                            <TrashIcon className="h-5 w-5" />
                                        </button>
                                    </div>
                                </div>
                                <div className="mt-6 pt-4 border-t border-gray-800/50 flex justify-between items-center">
                                    <p className="text-[10px] text-brand-muted font-black uppercase tracking-widest">
                                        {new Date(opp.timestamp).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                                    </p>
                                    <button 
                                        onClick={() => handleViewApplicants(opp)}
                                        className="text-[10px] text-brand-orange font-black uppercase tracking-widest hover:underline"
                                    >
                                        Review Applicants &rarr;
                                    </button>
                                </div>
                            </div>
                        </li>
                    ))}
                </ul>
            </div>
        )}
    </>
);

// --- Attending Tab Content ---
interface AttendingTabProps {
    myAttendingOpportunities: Opportunity[];
    handleViewOpportunity: (opp: Opportunity) => void;
}
const AttendingTabContent: React.FC<AttendingTabProps> = ({ myAttendingOpportunities, handleViewOpportunity }) => (
    <>
        {myAttendingOpportunities.length === 0 ? (
            <div className="bg-brand-surface rounded-lg p-12 text-center border border-dashed border-gray-800 shadow-sm">
                <MapPinIcon className="h-16 w-16 text-gray-700 mx-auto mb-4 opacity-30" />
                <h3 className="text-xl font-black text-white mb-2 tracking-tight uppercase">Not attending anything yet</h3>
                <p className="text-brand-muted max-w-sm mx-auto font-medium">Browse the feed for gigs, events, and shows you want to attend.</p>
            </div>
        ) : (
            <div className="bg-brand-surface rounded-lg p-4 border border-gray-800 shadow-sm">
                <ul className="space-y-3">
                    {myAttendingOpportunities.map(opp => (
                        <li key={opp.id}>
                            <div
                                className="block p-5 bg-gray-800/30 hover:bg-gray-800/60 border border-gray-700 hover:border-brand-orange/30 rounded-2xl cursor-pointer transition-all"
                                onClick={() => handleViewOpportunity(opp)}
                            >
                                <div className="flex justify-between items-start flex-wrap gap-2">
                                    <div>
                                        <h3 className="font-black text-lg text-white tracking-tight uppercase">{opp.title}</h3>
                                        <p className="text-xs text-brand-orange font-bold uppercase tracking-widest mt-1">by {opp.author.name}</p>
                                        <p className="text-[10px] text-brand-muted font-black uppercase tracking-widest mt-2">
                                            <MapPinIcon className="h-3 w-3 inline-block mr-1 -mt-0.5" />
                                            {opp.location}
                                        </p>
                                    </div>
                                    <div className="px-3 py-1 text-[10px] font-black uppercase tracking-widest rounded-full border bg-brand-orange/10 text-brand-orange border-brand-orange/30">
                                        {opp.type}
                                    </div>
                                </div>
                                {opp.startDate && (
                                    <div className="mt-4 pt-3 border-t border-gray-800/50 flex justify-between items-center">
                                        <p className="text-[10px] text-brand-muted font-black uppercase tracking-widest">
                                            {new Date(opp.startDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                                        </p>
                                        <span className="text-[10px] text-brand-orange font-black uppercase tracking-widest">View Details &rarr;</span>
                                    </div>
                                )}
                            </div>
                        </li>
                    ))}
                </ul>
            </div>
        )}
    </>
);

export default MyActivityPage;
